/* ============================================================
   ReviewsBlock (TASK-179) — provider reviews on the product
   page: public listing + review form for customers with a
   completed order from this seller + abuse reporting
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Review } from '../types/pricing';
import type { OrderSummary } from '../types/product';

const STATUS_COMPLETED = 4; // OrderStatus.Completed

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ru-RU');
  } catch {
    return iso.slice(0, 10);
  }
}

export function ReviewsBlock({ providerId, providerName }: { providerId: string; providerName: string }) {
  const { isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completedOrders, setCompletedOrders] = useState<OrderSummary[]>([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [orderId, setOrderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReviews(await api.getProviderReviews(providerId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки отзывов');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Eligible orders: completed, from this provider.
  useEffect(() => {
    if (!isAuthenticated) return;
    api.getMyOrders
      ? api
          .getMyOrders()
          .then((orders) =>
            setCompletedOrders(
              orders.filter(
                (o) =>
                  (o.status === STATUS_COMPLETED || o.status === 'Completed') &&
                  (o.providerId === providerId || o.providerId == null),
              ),
            ),
          )
          .catch(() => {})
      : undefined;
  }, [isAuthenticated, providerId]);

  const submit = async () => {
    if (!orderId) {
      setError('Выберите завершённый заказ');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.createReview(orderId, rating, comment.trim());
      setNotice('Спасибо! Отзыв опубликован.');
      setComment('');
      setOrderId('');
      setCompletedOrders((prev) => prev.filter((o) => o.orderId !== orderId));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить отзыв');
    } finally {
      setBusy(false);
    }
  };

  const report = async (reviewId: string) => {
    const reason = window.prompt('Причина жалобы на отзыв?');
    if (!reason) return;
    try {
      await api.reportReview(reviewId, reason);
      setNotice('Жалоба отправлена модераторам');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить жалобу');
    }
  };

  const avg = reviews.length > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : null;

  return (
    <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
          Отзывы об исполнителе
        </h2>
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {providerName}
          {avg !== null && ` · ${avg.toFixed(1)} ★ · ${reviews.length}`}
        </span>
      </div>

      {notice && <span className="badge badge-success">✓ {notice}</span>}
      {error && <span className="badge badge-danger">{error}</span>}

      {/* Review form: only with a completed order */}
      {isAuthenticated && completedOrders.length > 0 && (
        <div style={{ display: 'grid', gap: 'var(--space-sm)', padding: 'var(--space-md)', background: 'var(--color-bg-secondary, #F9F9F9)', borderRadius: 'var(--radius-md)' }}>
          <select value={orderId} onChange={(e) => setOrderId(e.target.value)} style={{ justifySelf: 'start' }}>
            <option value="">Выберите завершённый заказ…</option>
            {completedOrders.map((o) => (
              <option key={o.orderId} value={o.orderId}>
                Заказ от {formatDate(o.createdAtUtc)}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                style={{
                  fontSize: 20,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: star <= rating ? 'var(--color-warning, #FF9500)' : 'var(--color-separator)',
                }}
                aria-label={`Оценка ${star}`}
              >
                ★
              </button>
            ))}
          </div>
          <textarea
            placeholder="Как всё прошло?"
            value={comment}
            maxLength={2000}
            onChange={(e) => setComment(e.target.value)}
            style={{ minHeight: 72 }}
          />
          <button className="btn btn-primary" style={{ justifySelf: 'start' }} disabled={busy} onClick={submit}>
            {busy ? 'Отправка…' : 'Оставить отзыв'}
          </button>
        </div>
      )}

      {/* Public reviews */}
      {loading ? (
        <div className="spinner" style={{ margin: '0 auto' }} />
      ) : reviews.length === 0 ? (
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
          Пока нет отзывов.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-sm)' }}>
          {reviews.map((r) => (
            <div key={r.id} style={{ display: 'grid', gap: 'var(--space-xxs)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <span style={{ color: 'var(--color-warning, #FF9500)' }}>
                  {'★'.repeat(r.rating)}
                  <span style={{ color: 'var(--color-separator)' }}>{'★'.repeat(5 - r.rating)}</span>
                </span>
                <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)' }}>
                  {r.customerName}
                </span>
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  {formatDate(r.createdAtUtc)}
                </span>
                {isAuthenticated && (
                  <button
                    onClick={() => report(r.id)}
                    style={{ marginLeft: 'auto', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    ⚑ Пожаловаться
                  </button>
                )}
              </div>
              {r.comment && (
                <p style={{ fontSize: 'var(--fs-body)', lineHeight: 1.5, margin: 0 }}>{r.comment}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
